import { Component, inject, InjectionToken } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Route, Router } from '@angular/router';
import { ApiSlug, AppApi } from '@tytapp/api';
import { LoggerService, Redirection, Shell, buildQuery } from '@tytapp/common';
import { UserService } from '@tytapp/user';
import { SlugService } from './slug.service';
import { STATIC_SLUGS } from './static-slugs';
import { STATIC_REDIRECTS } from './static-redirects';
import { isClientSide } from '@tytapp/environment-utils';


interface ResolvedSlug {
    type: string;
    slug: string;
    id?: string;
    uuid?: string;
    redirect?: boolean;
    url?: string;
}

interface SlugOutletSettings {
    fallbackUrl?: string;
    masked?: boolean;
}

const OUTLET_SETTINGS = new InjectionToken<SlugOutletSettings>('SLUG_OUTLET_SETTINGS');

@Component({ template: `<app-spinner></app-spinner>` })
export class SlugComponent {
    private router = inject(Router);
    private next = inject(ActivatedRoute);
    private redirection = inject(Redirection);
    private userService = inject(UserService);
    private shell = inject(Shell);
    private logger = inject(LoggerService).configure({ source: 'slugc' });
    private slugs = inject(SlugService);
    private appApi = inject(AppApi);
    private settings = inject(OUTLET_SETTINGS, { optional: true }) || {};

    static outlet(path: string, settings: SlugOutletSettings = {}): Route {
        return {
            path,
            pathMatch: 'full',
            component: SlugComponent,
            providers: [
                {
                    provide: OUTLET_SETTINGS,
                    useValue: settings
                }
            ]
        }
    }

    static routes(): Route[] {
        let maxParams = 10;
        let routes: Route[] = [];
        for (let i = 0; i < maxParams; ++i) {
            routes.push(<Route>{
                path: `:slug${i > 0 ? `/${Array.from(Array(i)).map((_, i) => `:param${i + 1}`).join('/')}` : ``}`,
                component: SlugComponent,
                pathMatch: 'full',
                data: { reuse: false }
            });
        }

        return routes;
    }

    forwardedUrl: string;

    async nationHomeRedirect() {
        await this.userService.ready;
        const user = this.userService.user;

        const tytn_enabled = await this.shell.hasFeature('apps.web.enable_tytn');
        const openBeta = await this.shell.hasFeature('apps.web.nation_open_beta');

        const url = tytn_enabled && (openBeta || user?.can_access_tytnation) ?
            '/nation/home' :
            '/home';

        this.redirection.go(url, true);
    }

    async ngOnInit() {
        let next = this.next.snapshot;
        let originalUrl = '/' + next.url.join('/');
        const isSlugRoute = /:slug\b/.test(next.routeConfig.path);
        let pageUrl = this.router.routerState.snapshot.url;
        let pagePath = pageUrl.replace(/\?.*$/, '').replace(/^\/|\/$/g, '');

        // --- Corner case: empty slug ---
        // This should never happen, but if we are the built-in global Slug outlet route
        // and no slug parameter was provided, redirect to home.

        if (isSlugRoute && !next.paramMap.get('slug'))
            return this.nationHomeRedirect();

        // --- Cloudflare Redirects ---
        // Before we check for custom pages or global slugs, check if there is a Cloudflare redirect.

        if (isClientSide()) {
            let destinationUrl = await this.lookupRedirect(pagePath);
            if (destinationUrl) {
                this.logger.info(`Found a redirect to ${destinationUrl}`);
                this.redirection.go(destinationUrl);
                return;
            }
        }

        let resolvedSlug = await this.resolveRoute(pagePath);

        // --- Static Slugs/Redirects ---
        // - If no dynamic URL is found, look for a static redirect rule.
        // - Fallback to STATIC_SLUGS, since STATIC_REDIRECTS are more specific (ie /live/schedule vs /live)

        let url: string;
        let masked = this.settings.masked ?? true;

        if (resolvedSlug) {
            if (resolvedSlug.type === 'User') {
                if (!pagePath.startsWith('@')) {
                    debugger;
                    this.redirection.go(`/@${pagePath}`, true);
                    return;
                }
            } else if (resolvedSlug.type === 'CMS::Redirect') {
                this.redirection.go(resolvedSlug.url, true);
                return;
            }

            // Construct the internal URL.

            let newUrl = `${this.urlBySlugType(resolvedSlug)}`;
            if (resolvedSlug.type !== 'CMS::Page')
                newUrl += pagePath.split('/').slice(1).join('/');

            url = newUrl;
        } else {
            const slug = pagePath.split('/')[0];
            const staticRedirect = STATIC_REDIRECTS[pagePath.toLowerCase()];
            const staticSlug = STATIC_SLUGS[slug.toLowerCase()];

            if (staticRedirect) {
                if (typeof staticRedirect === 'string') {
                    url = staticRedirect;
                    masked = false;
                } else {
                    url = staticRedirect.url;
                    masked = staticRedirect.masked || false;
                }

                this.logger.info(`Using static redirect ${pagePath.toLowerCase()} to ${url}, masked=${masked}`);
            } else if (staticSlug) {
                url = `${this.urlBySlugType({ type: 'Navigation', slug: staticSlug, redirect: false })}`
                    + `${pagePath.split('/').slice(1).join('/')}`;
            } else {
                // When using SlugComponent.outlet(), it is possible to define a fallback URL directly, in the case
                // that a dynamic route is not available.

                if (!this.settings.fallbackUrl)
                    this.logger.error(`Could not resolve route ${pagePath}`);
                url = this.settings.fallbackUrl ?? `/not-found`;
            }
        }

        if (url === originalUrl) {
            this.logger.error(`Resolved internal URL (${url}) already matches current URL (${originalUrl})`);
            url = '/not-found';
        }

        if (masked) {
            this.logger.info(`Using masked navigation to ${url}`);
            this.navigateToInternalUrlFromRoute(url, next);
        } else {
            this.logger.info(`Using unmasked navigation to ${url}`);
            this.redirection.go(url, true);
        }
    }

    private navigateToInternalUrlFromRoute(url: string, next: ActivatedRouteSnapshot) {

        url = `${url}?${buildQuery(next.queryParams)}`

        this.navigateToInternalUrl(url, `/${next.url.join('/')}`);
    }

    private navigateToInternalUrl(url: string, slugUrl: string) {
        this.forwardedUrl = url;
        this.logger.log(`Internal URL: ${url}`);
        this.router.navigateByUrl(url, {
            skipLocationChange: true,
            state: {
                slugged: true,
                slugUrl
            }
        });
    }

    private async lookupRedirect(url: string): Promise<string | undefined> {
        try {
            return (<{ url: string }>await this.appApi.lookupRedirect(url).toPromise())?.url;
        } catch (e) {
            if (e.statusCode !== 404) {
                if (e.json)
                    e = e.json();

                this.logger.error(`Failed to lookup redirect:`);
                this.logger.error(e);
            }
        }

        return undefined;
    }

    urlBySlugType(slug: ResolvedSlug) {
        try {
            return slug ? this.slugs.getContentUrl(slug) : `/not-found`;
        } catch (e) {
            this.logger.error(`Failed to get content URL for slug ${slug.type ?? 'unknown'}[${slug.slug ?? 'none'}]: ${e.stack || e}`);
            return `/not-found`;
        }
    }

    async resolveRoute(url: string): Promise<ResolvedSlug> {
        if (!url.startsWith('/'))
            url = `/${url}`;

        if (url.startsWith('/@'))
            return { slug: url.slice(2), type: 'User' };

        // Support all case sensitive variants for /live
        // TODO: this shouldn't be needed due to STATIC_SLUGS having `live`

        if (url.toLowerCase() === 'live') {
            return { type: 'Navigation', slug: '/live' };
        }

        let result: ApiSlug;

        try {
            result = await this.slugs.resolveRoute(url);
        } catch (e) {
            this.logger.error(`Caught error while resolving slug:`);
            this.logger.error(e);
            return null;
        }

        if (!result || result.type === 'User')
            return null;

        return <ResolvedSlug> result;
    }

}
